import { InfiniteList } from '@src/design-system/molecules/infinite-list';
import { ItemInfo } from '@features/tasks/pages/create/item-info';

import type { TMember } from '@shared/types/member';
import { useRef, type ComponentProps, type ElementType } from 'react';
import memberApi, { type TMemberQuery } from '@shared/services/member-api';
import type createCrudService from '@core/api/create-crud-service';
import type {
  TWorkspaceMember,
  TWorkspaceMemberQuery,
} from '@features/tasks/services/workspace-members-api';

type TMemberItem = TMember | TWorkspaceMember;

type TMemberQueryUnion = TMemberQuery | TWorkspaceMemberQuery;

export const MembersList = ({
  onClickItem,
  isItemSelected,
  CLoseListTrigger,
  fetchInfiniteData,
  props,
}: {
  onClickItem: (item: TMember, isSelected: boolean) => void;
  isItemSelected?: (item: TMember) => boolean;
  CLoseListTrigger?: ElementType;
  fetchInfiniteData?:
  | ReturnType<
    typeof createCrudService<TMember, TMemberQuery>
  >['useFetchInfinite']
  | ReturnType<
    typeof createCrudService<TWorkspaceMember, TWorkspaceMemberQuery>
  >['useFetchInfinite'];
  props?: {
    infiniteListProps?: Partial<
      ComponentProps<typeof InfiniteList<TMemberItem, TMemberQueryUnion>>
    >;
    infoItemProps?: Partial<ComponentProps<typeof ItemInfo<TMember>>>;
    /** Wrapper for each item (e.g. PopoverClose to close popover on select) */
    itemWrapper?: (props: { children: React.ReactNode }) => React.ReactNode;
  };
}) => {
  const ref = useRef<HTMLButtonElement>(null);

  // Transform TWorkspaceMember to TMember, or return TMember as-is
  const transformToMember = (item: TMemberItem): TMember => {
    if ('member' in item) {
      // It's TWorkspaceMember, extract the member
      return item.member;
    }
    // It's already TMember
    return item;
  };

  return (
    <>
      <InfiniteList
        fetchInfiniteData={fetchInfiniteData || memberApi.useFetchInfinite}
        query={{
          type: 'EMPLOYEE',
        }}
        searchParamName='memberName'
        enableSearch
        transformItems={(items) => items.map(transformToMember)}
        // will check later
        props={{
          listContainerProps: { className: 'h-[300px] p-2' },
        }}
        renderItem={(item) => {
          const member = transformToMember(item);
          const isSelected = isItemSelected?.(member);
          const user = member.user;
          const content = (
            <ItemInfo
              item={member}
              onClick={() => onClickItem(member, !!isSelected)}
              isSelected={isSelected}
              description={user.email}
              name={`${user.first_name} ${user.last_name}`}
              avatar={user.avatar}
              withAvatar
              withCheckbox
              id={user.id}
              {...props?.infoItemProps}
            />
          );
          return props?.itemWrapper ? props.itemWrapper({ children: content }) : content;
        }}
        {...props?.infiniteListProps}
      />
      {CLoseListTrigger && (
        <CLoseListTrigger
          ref={ref}
          className='hidden'
        />
      )}
    </>
  );
};
